<script setup>
import { useLanguageStore } from "@/stores/languageStore.js";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useCustomStarMapStore } from "@/stores/customStarMapStore.js";
import StarMapWorkshopNav from "@/components/StarMapWorkshopNav.vue";
import { localizedCreatorText } from "@/utils/localizedCreatorText.js";

const router = useRouter();
const languageStore = useLanguageStore();
const store = useCustomStarMapStore();
const selectedMapId = ref("");
const searchQuery = ref("");
const loading = ref(false);

const maps = computed(() => store.maps);
const filteredMaps = computed(() => {
  const keyword = searchQuery.value.trim().toLocaleLowerCase();
  if (!keyword) return maps.value;
  return maps.value.filter(map => {
    const haystack = `${map.name || ""} ${map.description || ""}`.toLocaleLowerCase();
    return haystack.includes(keyword);
  });
});
const selectedMap = computed(() => store.getMap(selectedMapId.value));
const selectedNodes = computed(() => selectedMap.value?.nodes || []);
const displayName = map => localizedCreatorText(languageStore, map?.name, "name");

async function chooseMap(map) {
  selectedMapId.value = map.id;
  store.setActiveMap(map.id);
  if (store.backendEnabled) {
    try {
      await store.loadRemoteProgress(map.id);
    } catch {
      // Selection itself remains usable even when progress refresh fails.
    }
  }
}

function editLevel(node) {
  if (!selectedMap.value) return;
  router.push({
    name: "custom-level-editor",
    params: { mapId: selectedMap.value.id, level: node.level },
    query: { from: "level-map-selector" }
  });
}

function builtCount(map) {
  return map.nodes.filter(node => store.isLevelBuilt(map.id, node.level)).length;
}

onMounted(async () => {
  if (!store.backendEnabled) {
    store.refresh();
  } else {
    loading.value = true;
    try {
      await store.loadRemoteMaps();
      await store.loadRemoteChallengeMap();
    } finally {
      loading.value = false;
    }
  }

  if (store.activeMap?.id && store.getMap(store.activeMap.id)) {
    selectedMapId.value = store.activeMap.id;
  } else if (store.maps[0]?.id) {
    selectedMapId.value = store.maps[0].id;
    store.setActiveMap(store.maps[0].id);
  }

  if (selectedMapId.value && store.backendEnabled) {
    try {
      await store.loadRemoteProgress(selectedMapId.value);
    } catch {
      // Editing remains available if the progress endpoint is temporarily unavailable.
    }
  }
});
</script>

<template>
  <section class="selector page-card">
    <div class="page-header subpage-header">
      <button class="back-button" type="button" @click="router.push({ name: 'workshop' })">← {{ languageStore.t("back") }}</button>
    </div>

    <StarMapWorkshopNav />

    <template v-if="maps.length">
      <div class="map-section">
        <div class="map-section-head">
          <h2>1. {{ languageStore.t("challengeMapTitle") }}</h2>
          <span v-if="searchQuery">{{ filteredMaps.length }} · {{ languageStore.t("myCustomMaps") }}</span>
        </div>
        <div class="map-search" role="search">
          <span class="search-icon" aria-hidden="true">⌕</span>
          <input
            v-model="searchQuery"
            type="search"
            inputmode="search"
            autocomplete="off"
            :placeholder="languageStore.t('searchMaps')"
            :aria-label="languageStore.t('searchMaps')"
          />
          <button v-if="searchQuery" class="search-clear" type="button" :aria-label="languageStore.t('clearSearch')" @click="searchQuery = ''">×</button>
        </div>
        <div v-if="filteredMaps.length" class="map-strip">
          <button
            v-for="map in filteredMaps"
            :key="map.id"
            type="button"
            class="map-choice"
            :class="{ active: selectedMapId === map.id }"
            @click="chooseMap(map)"
          >
            <span class="map-thumb"><img :src="map.imageDataUrl" alt="" /></span>
            <span class="map-meta">
              <strong>{{ displayName(map) }}</strong>
              <small>{{ languageStore.t("builtLevels", { done: builtCount(map), total: map.nodes.length }) }}</small>
            </span>
          </button>
        </div>
        <div v-else class="search-empty">
          <strong>{{ languageStore.t("noMatches") }}</strong>
          <button type="button" @click="searchQuery = ''">{{ languageStore.t("myCustomMaps") }}</button>
        </div>
      </div>

      <div v-if="selectedMap" class="level-section">
        <div class="section-title">
          <div>
            <h2>2. {{ languageStore.t("levelSelect") }}</h2>
            <span>{{ displayName(selectedMap) }} · {{ languageStore.t("totalLevels", { count: selectedNodes.length }) }}</span>
          </div>
          <button type="button" class="open-map" @click="router.push({ name: 'custom-map-view', params: { mapId: selectedMap.id }, query: { from: 'level-map-selector' } })">{{ languageStore.t("viewCompletedMap") }}</button>
        </div>

        <div class="level-grid">
          <button
            v-for="node in selectedNodes"
            :key="node.id"
            type="button"
            class="level-card"
            :class="{ built: store.isLevelBuilt(selectedMap.id, node.level), lit: store.isLevelBuilt(selectedMap.id, node.level) }"
            @click="editLevel(node)"
          >
            <span class="level-number">{{ node.level }}</span>
<small v-if="store.isLevelBuilt(selectedMap.id, node.level)">{{ languageStore.t("levelBuilt") }}</small>
            <small v-else>{{ store.isLevelBuilt(selectedMap.id, node.level) ? languageStore.t("levelBuilt") : languageStore.t("unavailable") }}</small>
          </button>
        </div>
      </div>
    </template>

    <div v-else class="empty-state">
      <strong>{{ languageStore.t("noCustomMaps") }}</strong>
      <span></span>
      <button class="neon-button" type="button" @click="router.push({ name: 'star-map-editor', query: { from: 'level-map-selector' } })">{{ languageStore.t("starMapEditorTitle") }}</button>
    </div>
  </section>
</template>

<style scoped>
.selector { padding: 18px 22px 22px; min-height: min(780px, calc(100dvh - 48px)); }
.subpage-header { justify-content: flex-start; margin-bottom: 8px; }
.map-section, .level-section { width: min(100%, 820px); margin: 0 auto; }
h2 { margin: 0 0 9px; color: #eafffd; font-size: 14px; }
.map-section-head { display:flex; align-items:center; justify-content:space-between; gap:10px; }
.map-section-head > span { color:rgba(255,255,255,.42); font-size:10px; }
.map-search { position: relative; display:flex; align-items:center; gap:8px; min-height:40px; margin:0 0 9px; padding:0 11px; border:1px solid rgba(82,246,232,.24); border-radius:11px; background:rgba(4,25,41,.78); }
.search-icon { flex:0 0 auto; color:#77f8ef; font-size:19px; line-height:1; transform:rotate(-12deg); }
.map-search input { min-width:0; flex:1; height:38px; border:0; outline:0; color:#effffd; background:transparent; font:inherit; font-size:12px; }
.map-search input::placeholder { color:rgba(255,255,255,.38); }
.map-search input::-webkit-search-cancel-button { display:none; }
.search-clear { flex:0 0 27px; width:27px; height:27px; display:grid; place-items:center; border:0; border-radius:50%; padding:0; color:#cafffb; background:rgba(82,246,232,.10); font-size:18px; line-height:1; }
.search-empty { min-height:76px; display:flex; align-items:center; justify-content:center; gap:10px; color:rgba(255,255,255,.48); }
.search-empty strong { font-size:11px; font-weight:700; }
.search-empty button { border:1px solid rgba(82,246,232,.25); border-radius:999px; padding:6px 10px; color:#cffffb; background:rgba(5,31,47,.78); font-size:10px; }
.map-strip { display: flex; gap: 9px; overflow-x: auto; padding: 2px 1px 13px; }
.map-choice { flex: 0 0 190px; display: grid; grid-template-columns: 72px minmax(0,1fr); gap: 9px; align-items: center; padding: 8px; border: 1px solid rgba(82,246,232,.16); border-radius: 13px; color: #fff; background: rgba(4,25,41,.74); text-align: left; }
.map-choice.active { border-color: rgba(82,246,232,.8); background: rgba(10,53,68,.92); box-shadow: inset 0 0 0 1px rgba(82,246,232,.14); }
.map-thumb { width: 72px; aspect-ratio: 1.4; overflow: hidden; border-radius: 8px; background: #010711; }
.map-thumb img { width: 100%; height: 100%; object-fit: cover; }
.map-meta { min-width: 0; display: grid; gap: 4px; }
.map-meta strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; }
.map-meta small { color: rgba(255,255,255,.48); font-size: 9px; }
.level-section { margin-top: 12px; padding-top: 13px; border-top: 1px solid rgba(82,246,232,.12); }
.section-title { display: flex; align-items: end; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.section-title h2 { margin-bottom: 3px; }
.section-title span { color: rgba(255,255,255,.48); font-size: 10px; }
.open-map { border: 1px solid rgba(82,246,232,.24); border-radius: 999px; padding: 7px 10px; color: #cffffb; background: rgba(5,31,47,.78); font-size: 10px; font-weight: 800; }
.level-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(105px, 1fr)); gap: 8px; }
.level-card { min-height: 88px; padding: 10px; border: 1px solid rgba(116,151,170,.18); border-radius: 13px; color: #e9f3f6; background: rgba(4,20,34,.76); display: grid; align-content: center; justify-items: center; gap: 3px; }
.level-card.built { border-color: rgba(82,246,232,.42); background: rgba(5,43,56,.84); }
.level-card.lit { border-color: rgba(255,255,255,.46); }
.level-number { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 50%; background: rgba(93,117,132,.25); color: #fff; font-size: 12px; font-weight: 900; }
.level-card.built .level-number { color: #03191b; background: #65f3e8; }
.level-card strong { font-size: 10px; }
.level-card small { color: rgba(255,255,255,.45); font-size: 8px; }
.empty-state { min-height: 360px; display: grid; place-items: center; align-content: center; gap: 10px; text-align: center; color: rgba(255,255,255,.58); }
.empty-state strong { color: #fff; font-size: 18px; }
.empty-state span { max-width: 430px; font-size: 12px; line-height: 1.55; }
@media (max-width:560px) {
  .selector {
    width: 100%;
    min-height: calc(100dvh - 28px);
    padding: 10px 10px 12px;
    border-radius: 18px;
  }
  .subpage-header {
    align-items: center;
    margin-bottom: 5px;
  }
  .subpage-header .back-button {
    padding: 3px 0;
    white-space: nowrap;
  }
  .map-section-head h2 { font-size: 15px; margin-bottom: 7px; }
  .map-section-head > span { font-size: 11px; }
  .map-search { min-height: 46px; padding: 0 12px; margin-bottom: 10px; }
  .map-search input { height: 44px; font-size: 15px; }
  .search-icon { font-size: 22px; }
  .search-clear { width: 30px; height: 30px; flex-basis: 30px; }
  .map-choice { flex-basis: 164px; grid-template-columns: 60px minmax(0,1fr); }
  .map-thumb { width: 60px; }
  .level-grid { grid-template-columns: repeat(3,minmax(0,1fr)); }
  .section-title { align-items: center; }
}

/* REMOVE_RED_FRAME_REMAINS_V2_START */
.tile-subtitle,
.nav-subtitle,
.feature-subtitle,
.card-subtitle,
.map-subtitle,
.workshop-subtitle,
.empty-description,
.empty-hint,
.helper-text,
.form-hint,
.status-hint,
.description,
.description-text,
.description-label,
.map-description,
.map-description-label,
.map-description-text,
.generated-hint,
.generated-status,
.generation-hint,
.generation-status,
.special-level-card,
.special-level,
.special-chapter,
.chapter-endpoint {
  display: none !important;
}
/* REMOVE_RED_FRAME_REMAINS_V2_END */

</style>
